import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { format, parseISO } from 'date-fns';
import { CalendarBooking } from '../lib/bookings';
import { Resource } from '../lib/resources';
import { clockIn } from '../lib/timeclock';
import { Star, MapPin, Calendar as CalendarIcon, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Navigation, Circle, Clock, AlertCircle, Trash2 } from 'lucide-react';
import BookingCustomerInfo from './booking/BookingCustomerInfo';
import { Customer } from '../lib/customers';

import { usePermissionCheck } from '../hooks/usePermissionCheck';
import { GoogleMap, useJsApiLoader, Marker, OverlayView } from '@react-google-maps/api';
import { useSupabase } from '../contexts/SupabaseContext';

/** Stable reference for useJsApiLoader — inline `['places']` retriggers LoadScript every render. */
const googleMapsLibraries: ('places')[] = ['places'];

const BOOKING_MAP_PIN_PATH =
  'M12 0C7.31 0 3.5 3.81 3.5 8.5C3.5 14.88 12 24 12 24S20.5 14.88 20.5 8.5C20.5 3.81 16.69 0 12 0ZM12 11.5C10.34 11.5 9 10.16 9 8.5C9 6.84 10.34 5.5 12 5.5C13.66 5.5 15 6.84 15 8.5C15 10.16 13.66 11.5 12 11.5Z';

async function geocodeAddressViaRest(
  address: string
): Promise<{ lat: number; lng: number } | null> {
  const key = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
  if (!key?.trim()) return null;
  try {
    const res = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${encodeURIComponent(key)}`
    );
    const data = await res.json();
    if (data.status === 'OK' && data.results?.[0]?.geometry?.location) {
      const { lat, lng } = data.results[0].geometry.location;
      return { lat, lng };
    }
  } catch (err) {
    console.warn('📍 REST geocode failed:', err);
  }
  return null;
}
import { useBusiness } from '../contexts/BusinessContext';
import { useAuth } from '../hooks/useAuth';
import { addressMarkerService, AddressMarker } from '../services/addressMarkerService';
import CrmEmbeddedOpportunityModal from './admin-dashboard/CrmEmbeddedOpportunityModal';

interface GoogleCalendarEvent {
  id: string;
  title: string | null;
  start_time: string | null;
  end_time: string | null;
  all_day: boolean;
  location: string | null;
  description: string | null;
  resource_id: string | null;
}

interface DayViewProps {
  selectedDate: Date;
  resources: Resource[];
  bookings: CalendarBooking[];
  timeOffRequests: CalendarBooking[];
  fieldQuoteEvents?: CalendarBooking[];
  onBookingClick: (event: any) => void;
  onTimeSlotClick?: (
    date: Date,
    resourceId: string,
    anchorRect?: { top: number; left: number; width: number; height: number },
    endDate?: Date
  ) => void;
  onMapPinClick?: (event: any, e: React.MouseEvent) => void;
  isLoading: boolean;
  businessHours?: any;
  resourceAvailabilities?: any;
  bookingFeedback?: Map<string, { rating: number; hasText: boolean }>;
  selectedBusinessId?: string;
  mapViewEnabled?: boolean;
  salesPinsEnabled?: boolean;
  isDraggingMode?: boolean;
  draggedBookingId?: string;
  onDropBooking?: (date: Date, resourceId?: string) => void;
  googleCalendarEvents?: GoogleCalendarEvent[];
  onGcalEventClick?: (event: GoogleCalendarEvent) => void;
  /** Renders inside a panel (e.g. control centre) instead of full-screen calendar chrome. */
  embedded?: boolean;
  /** When set, pans the map to this resource's live location pin. */
  focusResourceId?: string | null;
  /** Bumped to re-trigger focus when the same resource is selected again. */
  focusResourceNonce?: number;
}

export default function DayView({
  selectedDate,
  resources,
  bookings,
  timeOffRequests,
  fieldQuoteEvents = [],
  onBookingClick,
  onTimeSlotClick,
  onMapPinClick,
  isLoading,
  businessHours,
  resourceAvailabilities,
  bookingFeedback = new Map(),
  selectedBusinessId,
  mapViewEnabled = false,
  salesPinsEnabled: salesPinsEnabledProp = true,
  isDraggingMode = false,
  draggedBookingId,
  onDropBooking,
  googleCalendarEvents = [],
  onGcalEventClick,
  embedded = false,
  focusResourceId = null,
  focusResourceNonce = 0,
}: DayViewProps) {
  const { canAccess, currentResource } = usePermissionCheck();
  const { selectedBusiness } = useBusiness();
  const { user } = useAuth();
  const supabase = useSupabase();
  console.log('🎯 DayView FUNCTION CALLED with props:', {
    selectedDate: selectedDate?.toISOString(),
    resourcesLength: resources?.length || 0,
    bookingsLength: bookings?.length || 0,
    timeOffLength: timeOffRequests?.length || 0,
    fieldQuotesLength: fieldQuoteEvents?.length || 0,
    isLoading,
    selectedBusinessId,
    mapViewEnabled
  });
  
  const [statusDropdownOpen, setStatusDropdownOpen] = useState<string | null>(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState<string | null>(null);
  // Embedded control centre (Today's Schedule): always start expanded.
  // Full day view: start expanded only when the map is on.
  const [bookingsExpanded, setBookingsExpanded] = useState(
    embedded || mapViewEnabled
  );
  const [selectedMarker, setSelectedMarker] = useState<CalendarBooking | null>(null);
  const [selectedResourceMarker, setSelectedResourceMarker] = useState<any | null>(null);
  const [geocodedLocations, setGeocodedLocations] = useState<Map<string, { lat: number; lng: number }>>(new Map());
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);
  const [currentLocation, setCurrentLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  
  // Resource locations state (from location_updates table – append-only log)
  const [resourceLocations, setResourceLocations] = useState<Map<string, {
    id: string;
    resource_id: string;
    business_id?: string;
    lat: number;
    lng: number;
    accuracy?: number;
    heading?: number;
    speed?: number;
    activity?: string;
    activity_confidence?: number;
    odometer?: number;
    created_at?: string;
    gps_timestamp?: string;
    resources?: {
      id: string;
      first_name: string;
      last_name: string;
      avatar_url?: string;
      is_admin?: boolean;
    };
    // Legacy field from resource_locations – kept for compat but not present in location_updates
    resource?: {
      id: string;
      first_name: string;
      last_name: string;
      avatar_url?: string;
      is_admin?: boolean;
    };
  }>>(new Map());

  // On the Clock widget state
  const [isClockWidgetCollapsed, setIsClockWidgetCollapsed] = useState(() => {
    try { return localStorage.getItem('clock_widget_collapsed') === 'true'; } catch { return false; }
  });
  const [onTheClockResources, setOnTheClockResources] = useState<Array<{
    id: string;
    resource_id: string;
    clock_in: string;
    resource: {
      first_name: string;
      last_name: string;
      avatar_url?: string;
    };
  }>>([]);
  const [activeJobEntries, setActiveJobEntries] = useState<Array<{
    id: string;
    resource_id: string;
    booking_id: string;
    started_at: string;
    booking_number?: number;
    booking_title?: string;
  }>>([]);
  const [isAdminOrAdminResource, setIsAdminOrAdminResource] = useState<boolean>(false);
  const [currentTime, setCurrentTime] = useState<Date>(new Date()); // For live time updates

  // Clock-in prompt state (shown when hourly resource clicks navigate without being clocked in)
  const [clockInPromptOpen, setClockInPromptOpen] = useState(false);
  const [clockInPromptLoading, setClockInPromptLoading] = useState(false);
  const pendingMapPinEvent = useRef<{ event: any; e: React.MouseEvent } | null>(null);
  const fetchLocationsRef = useRef<(() => Promise<void>) | null>(null);
  const onTheClockResourcesRef = useRef(onTheClockResources);
  const mapWrapperRef = useRef<HTMLDivElement>(null);
  const bookingMarkersRef = useRef<google.maps.Marker[]>([]);
  const hasInitialFit = useRef(false);
  const userMapInteractedRef = useRef(false);
  const skipAutoZoomEventsRef = useRef(0);
  const lastFittedBookingPinCountRef = useRef(0);
  const mapFitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingFocusResourceIdRef = useRef<string | null>(null);

  // Sales pins state
  const [salesPins, setSalesPins] = useState<AddressMarker[]>([]);
  const [selectedSalesPin, setSelectedSalesPin] = useState<AddressMarker | null>(null);
  const [selectedSalesPinPosition, setSelectedSalesPinPosition] = useState<{ lat: number; lng: number } | null>(null);
  const [viewLeadOpportunityId, setViewLeadOpportunityId] = useState<number | null>(null);

  // Load Google Maps with Places library (required for address autocomplete in customer modals)
  const { isLoaded: isMapLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '',
    libraries: googleMapsLibraries,
  });

  // Load sales pins when map view is enabled and sales pins setting is on
  useEffect(() => {
    if (!mapViewEnabled || !salesPinsEnabledProp || !selectedBusinessId) return;
    const load = () => {
      addressMarkerService.getMarkers(selectedBusinessId)
        .then(markers => setSalesPins(markers))
        .catch(err => console.error('Error loading sales pins:', err));
    };
    load();
    // Refresh instantly when a pin is dropped from the FAB sales pin flow
    window.addEventListener('salesPinCreated', load);
    return () => window.removeEventListener('salesPinCreated', load);
  }, [mapViewEnabled, salesPinsEnabledProp, selectedBusinessId]);

  const handleMapPinWithClockCheck = useCallback((event: any, e: React.MouseEvent) => {
    // Skip check for non-resource users (business owner) or admin resources
    if (!currentResource || currentResource.is_admin) {
      onMapPinClick?.(event, e);
      return;
    }

    // Only prompt for hourly resources
    if (currentResource.compensation_type === 'hourly') {
      const isClockedIn = onTheClockResources.some(
        (entry: any) => entry.resource_id === currentResource.id
      );
      if (!isClockedIn) {
        pendingMapPinEvent.current = { event, e };
        setClockInPromptOpen(true);
        return;
      }
    }

    onMapPinClick?.(event, e);
  }, [currentResource, onTheClockResources, onMapPinClick]);

  const handleClockInFromPrompt = useCallback(async () => {
    if (!currentResource || !selectedBusinessId) return;
    setClockInPromptLoading(true);
    try {
      await clockIn(selectedBusinessId, { resourceId: currentResource.id, source: 'day_view' });

      setClockInPromptOpen(false);
      if (pendingMapPinEvent.current) {
        onMapPinClick?.(pendingMapPinEvent.current.event, pendingMapPinEvent.current.e);
        pendingMapPinEvent.current = null;
      }
    } catch (err) {
      console.error('Failed to clock in:', err);
      alert(err instanceof Error ? err.message : 'Failed to clock in. Please try again.');
    } finally {
      setClockInPromptLoading(false);
    }
  }, [currentResource, selectedBusinessId, onMapPinClick]);

  const handleSkipClockIn = useCallback(() => {
    setClockInPromptOpen(false);
    if (pendingMapPinEvent.current) {
      onMapPinClick?.(pendingMapPinEvent.current.event, pendingMapPinEvent.current.e);
      pendingMapPinEvent.current = null;
    }
  }, [onMapPinClick]);

  // Expand bookings sheet in full day view when map is enabled (not in embedded control centre)
  useEffect(() => {
    if (mapViewEnabled && !embedded) {
      setBookingsExpanded(true);
    }
  }, [mapViewEnabled, embedded]);

  // Current user's own location: read directly from the Transistorsoft plugin
  // via the clean getPosition() API. Polls every 5s for a responsive live dot.
  // persist = !isAdmin so regular resources push accurate GPS to DB,
  // while admins only see their own dot client-side.
  useEffect(() => {
    if (!mapViewEnabled || !currentResource?.id) return;

    const isAdmin = currentResource?.is_admin === true;

    let cancelled = false;
    let pollTimer: ReturnType<typeof setInterval> | null = null;

    const readMyPosition = async () => {
      try {
        const { getPosition } = await import('../services/backgroundLocationService');
        const pos = await getPosition(!isAdmin); // persist for resources, not admins
        if (!cancelled && pos) {
          setCurrentLocation(pos);
        }
      } catch (err) {
        console.warn('📍 Own location read error:', err);
      }
    };

    // Fetch immediately, then poll every 5 seconds for a responsive live map
    readMyPosition();
    pollTimer = setInterval(readMyPosition, 5000);

    return () => {
      cancelled = true;
      if (pollTimer) clearInterval(pollTimer);
    };
  }, [mapViewEnabled, currentResource?.id]);

  // Callback when map loads
  const onMapLoad = useCallback((map: google.maps.Map) => {
    setMapInstance(map);
  }, []);

  // Only panning marks manual interaction — zoom_changed from fitBounds must not block auto-fit
  useEffect(() => {
    if (!mapInstance) return;

    const dragStartListener = mapInstance.addListener('dragstart', () => {
      userMapInteractedRef.current = true;
    });

    return () => {
      google.maps.event.removeListener(dragStartListener);
    };
  }, [mapInstance]);

  const focusResourcePin = useCallback(
    (resourceId: string): boolean => {
      const locationData = resourceLocations.get(resourceId);
      if (
        !mapInstance ||
        !isMapLoaded ||
        locationData?.lat == null ||
        locationData?.lng == null
      ) {
        return false;
      }

      userMapInteractedRef.current = true;
      mapInstance.panTo({ lat: locationData.lat, lng: locationData.lng });
      mapInstance.setZoom(16);
      setSelectedResourceMarker(locationData);
      return true;
    },
    [resourceLocations, mapInstance, isMapLoaded]
  );

  useEffect(() => {
    if (!focusResourceId || !mapViewEnabled) {
      pendingFocusResourceIdRef.current = null;
      return;
    }

    pendingFocusResourceIdRef.current = focusResourceId;

    if (focusResourcePin(focusResourceId)) {
      pendingFocusResourceIdRef.current = null;
      return;
    }

    if (fetchLocationsRef.current) {
      void fetchLocationsRef.current();
    }
  }, [focusResourceId, focusResourceNonce, mapViewEnabled, focusResourcePin]);

  useEffect(() => {
    const pendingId = pendingFocusResourceIdRef.current;
    if (!pendingId || !mapViewEnabled) return;
    if (focusResourcePin(pendingId)) {
      pendingFocusResourceIdRef.current = null;
    }
  }, [resourceLocations, mapViewEnabled, focusResourcePin]);
  
  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'scheduled': return 'Scheduled';
      case 'in_progress': return 'In Progress';
      case 'completed': return 'Completed';
      case 'cancelled': return 'Cancelled';
      default: return status;
    }
  };
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-10 h-10 sm:w-12 sm:h-12 border-2 sm:border-[3px] border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-3 sm:mt-4 text-[14px] sm:text-[15px] text-gray-600 font-medium" style={{ letterSpacing: '-0.01em' }}>Loading bookings...</p>
        </div>
      </div>
    );
  }
  
  // Get bookings for the selected day
  console.log('📅 DayView received date:', {
    selectedDate: selectedDate,
    iso: selectedDate.toISOString(),
    local: selectedDate.toString(),
    formatted: format(selectedDate, 'yyyy-MM-dd'),
    display: format(selectedDate, 'EEEE, MMMM d, yyyy')
  });
  
  const selectedDateStr = format(selectedDate, 'yyyy-MM-dd');
  console.log('📦 DayView filtering bookings:', {
    selectedDate: selectedDateStr,
    totalBookings: bookings.length,
    timeOffRequests: timeOffRequests.length,
    fieldQuoteEvents: fieldQuoteEvents.length,
    selectedDateObj: selectedDate.toString()
  });
  
  // Combine all event types (bookings, time off, field quotes), excluding buffer blocks
  const todaysEvents = useMemo(() => {
  const allEvents = [...bookings, ...timeOffRequests, ...fieldQuoteEvents]
    .filter(event => event.extendedProps?.is_buffer_block !== true);
  
  return allEvents.filter(booking => {
    // Parse booking start and end times - handle both ISO strings and Date objects
    const bookingStart = typeof booking.start === 'string' 
      ? parseISO(booking.start) 
      : new Date(booking.start);
    
    const bookingEnd = typeof booking.end === 'string'
      ? parseISO(booking.end)
      : new Date(booking.end);
    
    // Get the start and end of the selected day
    const dayStart = new Date(selectedDate);
    dayStart.setHours(0, 0, 0, 0);
    
    const dayEnd = new Date(selectedDate);
    dayEnd.setHours(23, 59, 59, 999);
    
    // Check if booking overlaps with the selected day
    // A booking overlaps if: bookingStart <= dayEnd AND bookingEnd >= dayStart
    const overlaps = bookingStart <= dayEnd && bookingEnd >= dayStart;
    
    if (overlaps) {
      console.log('  ✅ Found booking:', booking.title, {
        bookingStart: format(bookingStart, 'yyyy-MM-dd HH:mm'),
        bookingEnd: format(bookingEnd, 'yyyy-MM-dd HH:mm'),
        selectedDate: selectedDateStr,
        dayStart: format(dayStart, 'yyyy-MM-dd HH:mm'),
        dayEnd: format(dayEnd, 'yyyy-MM-dd HH:mm'),
        overlaps: true
      });
    } else if (bookings.indexOf(booking) < 3) {
      // Log first few non-matching bookings for debugging
      console.log('  ❌ Booking not matching:', booking.title, {
        bookingStart: format(bookingStart, 'yyyy-MM-dd HH:mm'),
        bookingEnd: format(bookingEnd, 'yyyy-MM-dd HH:mm'),
        selectedDate: selectedDateStr,
        dayStart: format(dayStart, 'yyyy-MM-dd HH:mm'),
        dayEnd: format(dayEnd, 'yyyy-MM-dd HH:mm'),
        overlaps: false
      });
    }
    
    return overlaps;
  }).sort((a, b) => {
    const aStart = typeof a.start === 'string' ? parseISO(a.start) : new Date(a.start);
    const bStart = typeof b.start === 'string' ? parseISO(b.start) : new Date(b.start);
    return aStart.getTime() - bStart.getTime();
  });
  }, [bookings, timeOffRequests, fieldQuoteEvents, selectedDateStr, selectedDate]);
  
  console.log('📋 Bookings for today:', todaysEvents.length);

  const parseMapCoordinate = (value: unknown): number | null => {
    if (value == null || value === '') return null;
    const num = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(num) ? num : null;
  };

  const isMapPinEligibleEvent = useCallback((event: CalendarBooking) => {
    if (
      event.extendedProps?.is_time_off ||
      event.extendedProps?.is_buffer_block ||
      event.extendedProps?.is_task
    ) {
      return false;
    }
    if (event.extendedProps?.is_field_quote) return true;
    if (event.extendedProps?.mobile_booking) return true;

    const lat = parseMapCoordinate(event.extendedProps?.lat);
    const lng = parseMapCoordinate(event.extendedProps?.lng);
    if (lat != null && lng != null) return true;

    const address = event.extendedProps?.address || event.extendedProps?.booking_address;
    return !!address;
  }, []);

  const getEventMapPosition = useCallback(
    (event: CalendarBooking): { lat: number; lng: number } | null => {
      const lat = parseMapCoordinate(event.extendedProps?.lat);
      const lng = parseMapCoordinate(event.extendedProps?.lng);
      if (lat != null && lng != null) {
        return { lat, lng };
      }
      return geocodedLocations.get(event.id) ?? null;
    },
    [geocodedLocations]
  );

  const getBookingPinColor = useCallback((event: CalendarBooking) => {
    const status = event.extendedProps?.status;
    const isFieldQuote = !!event.extendedProps?.is_field_quote;
    const isMeasurement = !!event.extendedProps?.is_measurement;
    if (isFieldQuote) {
      return status === 'completed'
        ? '#22c55e'
        : isMeasurement
          ? '#8b5cf6'
          : '#f97316';
    }
    return status === 'completed'
      ? '#22c55e'
      : status === 'in_progress'
        ? '#f59e0b'
        : status === 'cancelled'
          ? '#ef4444'
          : '#3b82f6';
  }, []);

  const bookingMapPins = useMemo(() => {
    return todaysEvents
      .filter((event) => isMapPinEligibleEvent(event))
      .map((event) => {
        const position = getEventMapPosition(event);
        if (!position) return null;
        return {
          event,
          position,
          color: getBookingPinColor(event),
        };
      })
      .filter((pin): pin is NonNullable<typeof pin> => pin != null);
  }, [todaysEvents, isMapPinEligibleEvent, getEventMapPosition, getBookingPinColor]);

  // Native markers — React <Marker> + custom SVG paths are unreliable here; staff pins use simple circles via React.
  useEffect(() => {
    if (!mapInstance || !isMapLoaded || !mapViewEnabled) return;

    bookingMarkersRef.current.forEach((m) => m.setMap(null));
    bookingMarkersRef.current = [];

    if (typeof google === 'undefined' || !google.maps?.Point) return;

    for (const { event, position, color } of bookingMapPins) {
      if (selectedMarker?.id === event.id) continue;

      const marker = new google.maps.Marker({
        map: mapInstance,
        position,
        zIndex: 1000,
        icon: {
          path: BOOKING_MAP_PIN_PATH,
          fillColor: color,
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 2,
          scale: 1.5,
          anchor: new google.maps.Point(12, 24),
        },
      });

      marker.addListener('click', () => {
        setSelectedSalesPin(null);
        setSelectedSalesPinPosition(null);
        setSelectedMarker(event);
      });

      bookingMarkersRef.current.push(marker);
    }

    return () => {
      bookingMarkersRef.current.forEach((m) => m.setMap(null));
      bookingMarkersRef.current = [];
    };
  }, [mapInstance, isMapLoaded, mapViewEnabled, bookingMapPins, selectedMarker?.id]);

  // Get mobile bookings with addresses for map view
  const mobileBookingsWithAddresses = todaysEvents.filter(event => 
    event.extendedProps?.mobile_booking && 
    (event.extendedProps?.address || event.extendedProps?.booking_address || 
     (event.extendedProps?.lat && event.extendedProps?.lng))
  );
  
  console.log('📍 Mobile bookings for map:', mobileBookingsWithAddresses.length, mobileBookingsWithAddresses.map(b => ({
    id: b.id,
    title: b.title,
    address: b.extendedProps?.address,
    booking_address: b.extendedProps?.booking_address,
    lat: b.extendedProps?.lat,
    lng: b.extendedProps?.lng
  })));

  const geocodedIdsRef = useRef<Set<string>>(new Set());
  const pendingGeocodesRef = useRef(0);

  useEffect(() => {
    geocodedIdsRef.current = new Set();
    setGeocodedLocations(new Map());
  }, [selectedDate]);

  // Geocode addresses that don't have lat/lng
  useEffect(() => {
    if (!isMapLoaded || !mapViewEnabled) return;

    const geocoder = new google.maps.Geocoder();

    const bookingsToGeocode = todaysEvents.filter((event) => {
      if (!isMapPinEligibleEvent(event)) return false;

      const lat = parseMapCoordinate(event.extendedProps?.lat);
      const lng = parseMapCoordinate(event.extendedProps?.lng);
      if (lat != null && lng != null) return false;

      if (geocodedIdsRef.current.has(event.id)) return false;

      const address = event.extendedProps?.address || event.extendedProps?.booking_address;
      return !!address;
    });

    pendingGeocodesRef.current = bookingsToGeocode.length;

    bookingsToGeocode.forEach((booking) => {
      const address = booking.extendedProps?.address || booking.extendedProps?.booking_address;
      if (!address) return;

      geocodedIdsRef.current.add(booking.id);

      geocoder.geocode({ address, region: 'ca' }, async (results, status) => {
        pendingGeocodesRef.current = Math.max(0, pendingGeocodesRef.current - 1);

        const applyCoords = (lat: number, lng: number) => {
          setGeocodedLocations((prev) => {
            const newMap = new Map(prev);
            newMap.set(booking.id, { lat, lng });
            return newMap;
          });
        };

        if (status === 'OK' && results?.[0]) {
          const location = results[0].geometry.location;
          applyCoords(location.lat(), location.lng());
          return;
        }

        const restCoords = await geocodeAddressViaRest(address);
        if (restCoords) {
          applyCoords(restCoords.lat, restCoords.lng);
          return;
        }

        geocodedIdsRef.current.delete(booking.id);
        console.error('📍 Geocoding failed for', address, ':', status);
      });
    });
  }, [isMapLoaded, mapViewEnabled, todaysEvents, isMapPinEligibleEvent]);

  // Fetch latest resource locations from location_updates (append-only log)
  // and subscribe to realtime INSERT events for live map updates.
  // We use location_updates instead of resource_locations because:
  //  - INSERTs are 100% reliable with Supabase Realtime (no REPLICA IDENTITY FULL needed)
  //  - The table is confirmed to always receive fresh data from the edge function
  useEffect(() => {
    if (!mapViewEnabled || !selectedBusinessId || !supabase) return;

    console.log('📍 Setting up location_updates subscription for business:', selectedBusinessId);

    // Cache of resource details so we only fetch once per resource
    const resourceCache = new Map<string, any>();

    // Helper: fetch resource details and attach to a location entry
    const attachResourceDetails = async (location: any): Promise<any> => {
      const rid = location.resource_id;
      if (resourceCache.has(rid)) {
        return { ...location, resources: resourceCache.get(rid) };
      }
      const { data: resource, error } = await supabase
        .from('resources')
        .select('id, first_name, last_name, avatar_url, is_admin')
        .eq('id', rid)
        .single();
      if (error || !resource) {
        console.warn('📍 Could not fetch resource details for:', rid, error?.message);
        return location;
      }
      resourceCache.set(rid, resource);
      return { ...location, resources: resource };
    };

    // Fetch the single most recent location entry for each clocked-in resource.
    // This guarantees every clocked-in resource always has a pin, regardless of
    // how long ago their last GPS update was.
    const fetchInitialLocations = async () => {
      try {
        // Get the list of currently clocked-in resource IDs (read from ref for freshness).
        // If the widget hasn't loaded yet, fall back to a broad query for this business.
        const clockedInIds: string[] = (onTheClockResourcesRef.current || []).map((e: any) => e.resource_id);

        if (clockedInIds.length === 0) {
          // No clocked-in resources yet — try a broad fetch as fallback
          // (widget may not have loaded yet on first render)
          const { data, error } = await supabase
            .from('location_updates')
            .select('*')
            .eq('business_id', selectedBusinessId)
            .order('created_at', { ascending: false })
            .limit(50);

          if (error) {
            console.error('📍 Error fetching location_updates (fallback):', error);
            return;
          }

          const latestByResource = new Map<string, any>();
          data?.forEach((row: any) => {
            if (!latestByResource.has(row.resource_id)) {
              latestByResource.set(row.resource_id, row);
            }
          });

          const locationsMap = new Map();
          await Promise.all(
            Array.from(latestByResource.values()).map(async (loc) => {
              const enriched = await attachResourceDetails(loc);
              locationsMap.set(loc.resource_id, enriched);
            })
          );

          setResourceLocations(locationsMap);
          console.log('📍 Fallback fetch — resource locations:', locationsMap.size);
          return;
        }

        // Query the latest location for each clocked-in resource individually
        const locationsMap = new Map();
        await Promise.all(
          clockedInIds.map(async (resourceId) => {
            const { data, error } = await supabase
              .from('location_updates')
              .select('*')
              .eq('resource_id', resourceId)
              .eq('business_id', selectedBusinessId)
              .order('created_at', { ascending: false })
              .limit(1)
              .maybeSingle();

            if (error) {
              console.warn('📍 Error fetching location for resource:', resourceId, error.message);
              return;
            }
            if (!data) return;

            const enriched = await attachResourceDetails(data);
            locationsMap.set(resourceId, enriched);
          })
        );

        setResourceLocations(locationsMap);
        console.log('📍 Fetched locations for clocked-in resources:', {
          clockedIn: clockedInIds.length,
          withLocation: locationsMap.size,
        });
      } catch (error) {
        console.error('📍 Exception fetching location_updates:', error);
      }
    };

    fetchLocationsRef.current = fetchInitialLocations;
    fetchInitialLocations();

    // Subscribe to realtime INSERT events on location_updates
    const createSubscription = () => {
      return supabase
        .channel(`location-updates-${selectedBusinessId}-${Date.now()}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'location_updates',
            filter: `business_id=eq.${selectedBusinessId}`
          },
          async (payload) => {
            const location = payload.new as any;
            if (!location?.resource_id) return;

            console.log('📍 location_updates INSERT received:', {
              resource_id: location.resource_id,
              lat: location.lat,
              lng: location.lng,
              created_at: location.created_at,
            });

            if (location.business_id !== selectedBusinessId) return;

            const enriched = await attachResourceDetails(location);

            const resourceName = enriched.resources
              ? `${enriched.resources.first_name} ${enriched.resources.last_name}`
              : 'Unknown';
            console.log('📍 Updating map pin for:', resourceName);

            setResourceLocations(prev => {
              const newMap = new Map(prev);
              const existing = newMap.get(location.resource_id);
              if (existing) {
                const incomingTime = new Date(location.gps_timestamp || location.created_at).getTime();
                const currentTime = new Date(existing.gps_timestamp || existing.created_at).getTime();
                if (incomingTime <= currentTime) {
                  return prev;
                }
              }
              newMap.set(location.resource_id, enriched);
              return newMap;
            });
          }
        )
        .subscribe((status) => {
          console.log('📍 location_updates subscription status:', status);
        });
    };

    let activeSubscription = createSubscription();

    // Reconnect when app returns to foreground (iOS minimize/restore)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('📍 App returned to foreground — reconnecting location subscription');
        activeSubscription.unsubscribe();
        activeSubscription = createSubscription();
        fetchInitialLocations();
      }
    };

    const handleFocus = () => {
      console.log('📍 Window focused — reconnecting location subscription');
      activeSubscription.unsubscribe();
      activeSubscription = createSubscription();
      fetchInitialLocations();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      console.log('📍 Cleaning up location_updates subscription');
      activeSubscription.unsubscribe();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [mapViewEnabled, selectedBusinessId, supabase]);

  // Keep the ref in sync so the fetch closure always reads the latest clocked-in list.
  // When the clocked-in list changes (someone clocks in/out), refetch locations.
  useEffect(() => {
    onTheClockResourcesRef.current = onTheClockResources;
    if (fetchLocationsRef.current && onTheClockResources.length > 0) {
      fetchLocationsRef.current();
    }
  }, [onTheClockResources]);

  // Check if user is admin or admin resource, and fetch clocked-in resources
  useEffect(() => {
    if (!mapViewEnabled || !selectedBusinessId || !supabase || !user) return;

    const checkAdminAndLoadClockIns = async () => {
      try {
        // Check if user is business owner (admin)
        const { data: business } = await supabase
          .from('businesses')
          .select('owner_id')
          .eq('id', selectedBusinessId)
          .single();

        const isBusinessOwner = business?.owner_id === user.id;

        // Check if user is admin resource
        const { data: resource } = await supabase
          .from('resources')
          .select('is_admin')
          .eq('auth_user_id', user.id)
          .eq('business_id', selectedBusinessId)
          .maybeSingle();

        const isAdminResource = resource?.is_admin === true;
        const canViewClockIns = isBusinessOwner || isAdminResource;

        setIsAdminOrAdminResource(canViewClockIns);

        if (canViewClockIns) {
          // On the Clock widget: only show resources who are currently clocked in (open timeclock row).
          // Data comes from payroll_page only; location is used only for map dot/activity display.
          const { data: clockedInData, error: clockError } = await supabase
            .from('payroll_page')
            .select(`
              id,
              resource_id,
              clock_in,
              resources (
                first_name,
                last_name,
                avatar_url
              )
            `)
            .eq('business_id', selectedBusinessId)
            .eq('entry_type', 'timeclock')
            .is('clock_out', null)
            .order('clock_in', { ascending: false });

          if (clockError) {
            console.error('Error fetching clocked-in resources:', clockError);
            return;
          }

          console.log('🕐 Clocked-in resources data:', clockedInData);
          const deduped = (clockedInData || []).reduce((acc: any[], entry: any) => {
            const existing = acc.find(e => e.resource_id === entry.resource_id);
            if (!existing) {
              acc.push(entry);
            } else if (new Date(entry.clock_in) > new Date(existing.clock_in)) {
              acc[acc.indexOf(existing)] = entry;
            }
            return acc;
          }, []);
          setOnTheClockResources(deduped);

          // Fetch active job time entries to show which booking each resource is working on
          const { data: jobEntries } = await supabase
            .from('job_time_entries')
            .select('id, resource_id, booking_id, started_at')
            .eq('business_id', selectedBusinessId)
            .is('stopped_at', null);

          if (jobEntries && jobEntries.length > 0) {
            const bookingIds = [...new Set(jobEntries.map(e => e.booking_id))];
            const { data: bookings } = await supabase
              .from('bookings')
              .select('id, booking_number, title')
              .in('id', bookingIds);

            const bookingMap = new Map((bookings || []).map(b => [b.id, b]));
            setActiveJobEntries(jobEntries.map(e => {
              const b = bookingMap.get(e.booking_id);
              return { ...e, booking_number: b?.booking_number, booking_title: b?.title };
            }));
          } else {
            setActiveJobEntries([]);
          }
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
      }
    };

    checkAdminAndLoadClockIns();

    // Refresh every 10 seconds as a fallback in case realtime misses an event
    const interval = setInterval(checkAdminAndLoadClockIns, 10000);

    return () => clearInterval(interval);
  }, [mapViewEnabled, selectedBusinessId, supabase, user]);

  // Update current time every second for live elapsed time display
  useEffect(() => {
    if (!mapViewEnabled || !isAdminOrAdminResource) return;

    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000); // Update every second for live countdown

    return () => clearInterval(timer);
  }, [mapViewEnabled, isAdminOrAdminResource]);

  // Subscribe to payroll_page changes for real-time updates
  useEffect(() => {
    if (!mapViewEnabled || !selectedBusinessId || !supabase || !isAdminOrAdminResource) return;

    const subscription = supabase
      .channel(`on-the-clock-${selectedBusinessId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'payroll_page',
        },
        async () => {
          // Refresh On the Clock list when payroll_page changes
          // No filter on subscription — UPDATE/DELETE events require REPLICA IDENTITY FULL
          // for filters to work. The re-fetch below filters by business_id properly.
            const { data: clockedInData } = await supabase
            .from('payroll_page')
            .select(`
              id,
              resource_id,
              clock_in,
              resources (
                first_name,
                last_name,
                avatar_url
              )
            `)
            .eq('business_id', selectedBusinessId)
            .eq('entry_type', 'timeclock')
            .is('clock_out', null)
            .order('clock_in', { ascending: false });

          const deduped = (clockedInData || []).reduce((acc: any[], entry: any) => {
            const existing = acc.find(e => e.resource_id === entry.resource_id);
            if (!existing) {
              acc.push(entry);
            } else if (new Date(entry.clock_in) > new Date(existing.clock_in)) {
              acc[acc.indexOf(existing)] = entry;
            }
            return acc;
          }, []);
          setOnTheClockResources(deduped);
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [mapViewEnabled, selectedBusinessId, supabase, isAdminOrAdminResource]);

  // Map container style
  const mapContainerStyle = {
    width: '100%',
    height: '100%'
  };

  // Initial center/zoom live in options only — avoid controlled center/zoom props that
  // reset the viewport on every re-render (geocoding, live GPS) and undo fitBounds.
  const mapOptions = useMemo(() => ({
    center: { lat: 34.0522, lng: -118.2437 },
    zoom: 12,
    disableDefaultUI: true,
    zoomControl: false,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    scaleControl: false,
    rotateControl: false,
    clickableIcons: false,
    gestureHandling: 'greedy', // Allow single-finger pan on mobile
    styles: [
      {
        featureType: "poi",
        elementType: "labels",
        stylers: [{ visibility: "off" }]
      },
      {
        featureType: "transit",
        elementType: "labels",
        stylers: [{ visibility: "off" }]
      }
    ]
  }), []);

  // All pin positions for bounds fitting (bookings, staff, sales — same set as rendered markers)
  const allMarkerPositions = useMemo(() => {
    const positions: { lat: number; lng: number }[] = [];

    if (currentLocation) {
      positions.push(currentLocation);
    }

    todaysEvents.forEach((event) => {
      if (!isMapPinEligibleEvent(event)) return;
      const pos = getEventMapPosition(event);
      if (pos) positions.push(pos);
    });

    if (mapViewEnabled && isAdminOrAdminResource) {
      Array.from(resourceLocations.values()).forEach((location) => {
        if (location.business_id !== selectedBusinessId) return;
        if (currentResource?.id && location.resource_id === currentResource.id) return;
        const isClockedIn = onTheClockResources.some(
          (entry: { resource_id: string }) => entry.resource_id === location.resource_id
        );
        if (!isClockedIn || location.lat == null || location.lng == null) return;
        positions.push({ lat: location.lat, lng: location.lng });
      });
    }

    if (salesPinsEnabledProp) {
      salesPins.forEach((pin) => {
        positions.push({ lat: pin.latitude, lng: pin.longitude });
      });
    }

    return positions;
  }, [
    todaysEvents,
    isMapPinEligibleEvent,
    getEventMapPosition,
    currentLocation,
    resourceLocations,
    onTheClockResources,
    selectedBusinessId,
    currentResource?.id,
    mapViewEnabled,
    isAdminOrAdminResource,
    salesPins,
    salesPinsEnabledProp,
  ]);

  const fitMapToMarkers = useCallback((): boolean => {
    if (!mapInstance || !isMapLoaded || allMarkerPositions.length === 0) return false;
    if (userMapInteractedRef.current) return false;

    const bounds = new google.maps.LatLngBounds();
    allMarkerPositions.forEach((pos) => bounds.extend(pos));

    skipAutoZoomEventsRef.current = 4;
    mapInstance.fitBounds(bounds, {
      top: embedded ? 44 : 50,
      right: embedded ? 36 : 50,
      bottom: embedded ? (bookingsExpanded ? 200 : 48) : 150,
      left: embedded ? 36 : 50,
    });

    if (allMarkerPositions.length === 1) {
      skipAutoZoomEventsRef.current += 1;
      setTimeout(() => {
        if (!userMapInteractedRef.current) {
          mapInstance.setZoom(14);
        }
      }, 100);
    }

    return true;
  }, [mapInstance, isMapLoaded, allMarkerPositions, embedded, bookingsExpanded]);

  // Auto-fit: once on load (debounced for geocoding), once when booking pins first appear
  useEffect(() => {
    if (!mapInstance || !isMapLoaded || allMarkerPositions.length === 0) return;

    if (mapFitTimerRef.current) {
      clearTimeout(mapFitTimerRef.current);
    }

    mapFitTimerRef.current = setTimeout(() => {
      mapFitTimerRef.current = null;
      if (userMapInteractedRef.current) return;

      const bookingPinCount = todaysEvents.filter(
        (e) => isMapPinEligibleEvent(e) && getEventMapPosition(e) != null
      ).length;

      const hasAddressesToGeocode = todaysEvents.some((e) => {
        if (!isMapPinEligibleEvent(e)) return false;
        if (parseMapCoordinate(e.extendedProps?.lat) != null) return false;
        if (parseMapCoordinate(e.extendedProps?.lng) != null) return false;
        return !!(e.extendedProps?.address || e.extendedProps?.booking_address);
      });

      if (
        !hasInitialFit.current &&
        hasAddressesToGeocode &&
        pendingGeocodesRef.current > 0
      ) {
        return;
      }

      const needsInitialFit = !hasInitialFit.current;
      const needsBookingFit =
        hasInitialFit.current &&
        bookingPinCount > lastFittedBookingPinCountRef.current;

      if (!needsInitialFit && !needsBookingFit) return;

      const didFit = fitMapToMarkers();
      if (!didFit) return;

      hasInitialFit.current = true;
      if (bookingPinCount > 0) {
        lastFittedBookingPinCountRef.current = bookingPinCount;
      }
    }, embedded ? 600 : 250);

    return () => {
      if (mapFitTimerRef.current) {
        clearTimeout(mapFitTimerRef.current);
        mapFitTimerRef.current = null;
      }
    };
  }, [
    mapInstance,
    isMapLoaded,
    allMarkerPositions.length,
    geocodedLocations.size,
    todaysEvents.length,
    embedded,
    fitMapToMarkers,
    isMapPinEligibleEvent,
    getEventMapPosition,
  ]);

  // Embedded map: resize tiles when the panel layout changes (never re-fit bounds)
  useEffect(() => {
    if (!embedded || !mapInstance || !isMapLoaded) return;

    const refreshMapSize = () => {
      window.google?.maps?.event?.trigger(mapInstance, 'resize');
    };

    const t1 = setTimeout(refreshMapSize, 100);
    const t2 = setTimeout(refreshMapSize, 600);

    const observer = new ResizeObserver(() => refreshMapSize());
    if (mapWrapperRef.current) {
      observer.observe(mapWrapperRef.current);
    }

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      observer.disconnect();
    };
  }, [embedded, mapInstance, isMapLoaded, bookingsExpanded]);

  useEffect(() => {
    hasInitialFit.current = false;
    userMapInteractedRef.current = false;
    skipAutoZoomEventsRef.current = 0;
    lastFittedBookingPinCountRef.current = 0;
    pendingGeocodesRef.current = 0;
    if (mapFitTimerRef.current) {
      clearTimeout(mapFitTimerRef.current);
      mapFitTimerRef.current = null;
    }
  }, [selectedDate]);

  useEffect(() => {
    hasInitialFit.current = false;
    lastFittedBookingPinCountRef.current = 0;
    userMapInteractedRef.current = false;
  }, [bookings.length, fieldQuoteEvents.length]);

  const embeddedIosFont =
    "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', system-ui, sans-serif";

  const renderEmbeddedBookingsList = () => {
    const todaysGcalEvents = googleCalendarEvents.filter((ge) => {
      if (!ge.start_time) return false;
      const geStart = parseISO(ge.start_time);
      return format(geStart, 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd');
    });

    if (todaysEvents.length === 0 && todaysGcalEvents.length === 0) {
      return (
        <div
          className="flex flex-col items-center justify-center py-8 sm:py-10 px-4 text-center rounded-xl sm:rounded-2xl bg-white/80"
          style={{ fontFamily: embeddedIosFont }}
        >
          <CalendarIcon className="h-8 w-8 sm:h-9 sm:w-9 text-gray-300/90 mb-2" strokeWidth={1.5} />
          <p className="text-[12px] sm:text-[13px] font-medium text-gray-500">No bookings today</p>
        </div>
      );
    }

    const embeddedBookingCardClass =
      'w-full flex items-center gap-2 max-sm:gap-2 sm:gap-2.5 px-2.5 py-2 max-sm:px-2.5 max-sm:py-2 sm:px-3 sm:py-2.5 text-left rounded-xl sm:rounded-2xl bg-white shadow-[0_1px_2px_rgba(0,0,0,0.06),0_2px_8px_rgba(0,0,0,0.04)] border border-black/[0.04] active:bg-gray-100/90 transition-colors touch-manipulation min-h-[44px] sm:min-h-[52px]';

    return (
      <div className="space-y-3" style={{ fontFamily: embeddedIosFont }}>
        {todaysEvents.map((event, index) => renderFullBookingCard(event, index))}
        {todaysGcalEvents.map((ge) => {
          const startTime = ge.start_time ? parseISO(ge.start_time) : new Date();
          const endTime = ge.end_time ? parseISO(ge.end_time) : new Date(startTime.getTime() + 3600000);

          return (
            <button
              type="button"
              key={`gcal-${ge.id}`}
              className={`${embeddedBookingCardClass} min-h-[44px] sm:min-h-[48px]`}
              onClick={() => onGcalEventClick?.(ge)}
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <div className="w-2 h-2 rounded-full shrink-0 bg-[#4285F4]" />
              <div className="flex-1 min-w-0">
                <p className="text-[12px] sm:text-[13px] font-semibold text-gray-900 truncate leading-snug tracking-tight">
                  {ge.title || '(No title)'}
                </p>
                <p className="text-[11px] sm:text-[12px] text-gray-500 truncate leading-snug mt-0.5">
                  {format(startTime, 'h:mm a')} – {format(endTime, 'h:mm a')}
                  {ge.location ? ` · ${ge.location}` : ''}
                </p>
              </div>
              <span className="inline-flex shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-blue-50 text-[#4285F4]">
                GCal
              </span>
            </button>
          );
        })}
      </div>
    );
  };


  // Render the bookings list (used in both map and non-map views)
  const renderFullBookingCard = (event: CalendarBooking, index: number) => {
          const status = event.extendedProps.status;
          const isBufferBlock = event.extendedProps?.is_buffer_block === true;
          
          // Buffer block card
          if (isBufferBlock) {
            const startTime = new Date(event.start);
            const endTime = event.end ? new Date(event.end) : new Date(startTime.getTime() + 60 * 60 * 1000);
            
            return (
              <div 
                key={`buffer-${index}`} 
                className="bg-white rounded-[12px] border border-gray-200/40 shadow-sm transition-all cursor-pointer overflow-hidden touch-manipulation active:scale-[0.98]"
                onClick={() => onBookingClick({ event })}
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <div 
                  className="p-3 sm:p-3.5"
                  style={{ 
                    backgroundColor: '#1f2937',
                    borderTopLeftRadius: '12px',
                    borderTopRightRadius: '12px'
                  }}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0 mr-3">
                      <p className="text-[15px] sm:text-[16px] font-semibold text-white mb-1" style={{ letterSpacing: '-0.01em' }}>
                        Buffer Block
                      </p>
                      <p className="text-[13px] sm:text-[14px] font-medium mb-1 text-white/95" style={{ letterSpacing: '-0.01em' }}>
                        {format(startTime, 'h:mm a')} - {format(endTime, 'h:mm a')}
                      </p>
                      <p className="text-[13px] sm:text-[14px] text-white/85 font-medium" style={{ letterSpacing: '-0.01em' }}>
                        {event.extendedProps.buffer_time} min buffer
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          }
          
          // Regular booking card
          const backgroundColor = status === 'in_progress' ? '#f59e0b' : 
                                status === 'cancelled' ? '#ef4444' : 
                                event.backgroundColor;
          
          const startTime = new Date(event.start);
          const endTime = event.end ? new Date(event.end) : new Date(startTime.getTime() + 60 * 60 * 1000);
          
          // Check if booking spans multiple days
          const startDateStr = format(startTime, 'MMM d');
          const endDateStr = format(endTime, 'MMM d');
          const isMultiDay = startDateStr !== endDateStr;
          
          const isDraggedBooking = draggedBookingId === event.id;
          
          return (
            <div 
              key={index} 
              className={`bg-white rounded-[12px] border border-gray-200/40 shadow-sm transition-all cursor-pointer overflow-hidden touch-manipulation active:scale-[0.98] ${
                isDraggedBooking ? 'opacity-50 border-4 border-blue-500 border-dashed' : ''
              }`}
              onClick={() => onBookingClick({ event })}
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              {/* Booking header */}
              <div 
                className="p-3 sm:p-3.5"
                style={{ 
                  backgroundColor,
                  borderTopLeftRadius: '12px',
                  borderTopRightRadius: '12px'
                }}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0 mr-3">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-[15px] sm:text-[16px] font-semibold truncate text-white" style={{ letterSpacing: '-0.01em' }}>
                        {event.extendedProps.booking_number > 0 && `#${event.extendedProps.booking_number} `}{event.title}
                      </p>
                      
                      {/* Star rating */}
                      {status === 'completed' && bookingFeedback.get(event.id) && (
                        <div className="flex items-center bg-white/25 rounded-full px-2 py-0.5 flex-shrink-0">
                          <Star size={12} className="text-yellow-300 fill-current mr-1" />
                          <span className="text-white text-[11px] sm:text-[12px] font-semibold">
                            {bookingFeedback.get(event.id)!.rating.toFixed(1)}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    {/* Show full date/time for multi-day bookings, or just time for same-day bookings */}
                    {isMultiDay ? (
                      <div className="text-[13px] font-medium mb-1 text-white/95" style={{ letterSpacing: '-0.01em' }}>
                        <p className="mb-0.5">
                          Start: {format(startTime, 'MMM d, h:mm a')}
                        </p>
                        <p>
                          End: {format(endTime, 'MMM d, h:mm a')}
                        </p>
                      </div>
                    ) : (
                    <p className="text-[13px] sm:text-[14px] font-medium mb-1 text-white/95" style={{ letterSpacing: '-0.01em' }}>
                      {format(startTime, 'h:mm a')} - {format(endTime, 'h:mm a')}
                    </p>
                    )}
                    
                    {/* Status */}
                    <div className="text-[13px] sm:text-[14px] text-white/85 font-medium" style={{ letterSpacing: '-0.01em' }}>
                      {getStatusLabel(status)}
                    </div>
                    
                    {/* Resource & Asset Avatars */}
                    {(() => {
                      const resources = event.extendedProps?.resources || [];
                      const assets = event.extendedProps?.booking_assets || [];
                      const allItems = [
                        ...resources.map((r: any) => ({
                          id: r.id,
                          label: `${(r.first_name || '').charAt(0)}${(r.last_name || '').charAt(0)}`,
                          image: r.avatar_url,
                          alt: `${r.first_name || ''} ${r.last_name || ''}`,
                          isAsset: false
                        })),
                        ...assets.map((a: any) => ({
                          id: a.id || a.asset_id,
                          label: (() => {
                            const name = a.name || 'A';
                            const parts = name.trim().split(/\s+/);
                            if (parts.length >= 2) {
                              return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
                            }
                            return name.substring(0, 2).toUpperCase();
                          })(),
                          image: a.image_url,
                          alt: a.name || 'Asset',
                          isAsset: true
                        }))
                      ];
                      const visible = allItems.slice(0, 4);
                      const extraCount = Math.max(0, allItems.length - visible.length);
                      
                      if (visible.length === 0) return null;
                      
                      return (
                        <div className="flex items-center mt-2">
                          <div className="flex items-center">
                            {visible.map((item, idx) => (
                              <div
                                key={item.id || idx}
                                className="rounded-full overflow-hidden flex items-center justify-center"
                                style={{
                                  width: 24,
                                  height: 24,
                                  marginLeft: idx === 0 ? 0 : -6,
                                  zIndex: 10 - idx,
                                  backgroundColor: item.isAsset ? 'rgba(220,230,255,0.95)' : 'rgba(255,255,255,0.9)',
                                  boxShadow: '0 0 0 2px rgba(255,255,255,0.3)'
                                }}
                              >
                                {item.image ? (
                                  <img src={item.image} alt={item.alt} className="w-full h-full object-cover" />
                                ) : (
                                  <span
                                    className={`text-[9px] font-semibold ${item.isAsset ? 'text-blue-700' : 'text-gray-800'}`}
                                    style={{ lineHeight: 1 }}
                                  >
                                    {item.label}
                                  </span>
                                )}
                              </div>
                            ))}

                            {extraCount > 0 && (
                              <div
                                className="rounded-full text-white flex items-center justify-center text-[9px] font-semibold"
                                style={{
                                  width: 24,
                                  height: 24,
                                  marginLeft: -6,
                                  backgroundColor: 'rgba(17,24,39,0.85)',
                                  boxShadow: '0 0 0 2px rgba(255,255,255,0.3)'
                                }}
                              >
                                +{extraCount}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                  
                  {canAccess.bookingSalesInfo && (
                    <div className="flex flex-col items-end flex-shrink-0">
                      {event.extendedProps.payment_status && (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] sm:text-[11px] font-bold mb-1 ${
                          event.extendedProps.payment_status === 'paid' 
                            ? 'bg-green-400/30 text-white' 
                            : 'bg-white/20 text-white'
                        }`}>
                          {event.extendedProps.payment_status === 'paid' ? 'PAID' : 'UNPAID'}
                        </span>
                      )}
                      {event.extendedProps.total_amount && (
                        <span className="text-[16px] sm:text-[18px] font-bold text-white" style={{ letterSpacing: '-0.01em' }}>
                          ${Number(event.extendedProps.total_amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Customer info section */}
              <div className="p-2.5 sm:p-3 bg-white">
                <div className="flex items-center gap-2">
                  {/* Map button - left side */}
                  {event.extendedProps.booking_address && (
                    <button 
                      className="w-8 self-stretch rounded-[8px] border border-gray-200/60 bg-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 active:bg-gray-100 active:scale-95 transition-all duration-150 flex items-center justify-center touch-manipulation flex-shrink-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMapPinWithClockCheck(event, e);
                      }}
                      title="Open in Google Maps"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <MapPin className="h-5 w-5" />
                    </button>
                  )}

                  {/* Customer info component */}
                  <div className="flex-1 min-w-0">
                    {(() => {
                      const customerName = event.extendedProps.customer || 'No Customer';
                      const nameParts = customerName.split(' ');
                      const firstName = nameParts[0] || '';
                      const lastName = nameParts.slice(1).join(' ') || '';
                      
                      const customer: Customer = {
                        id: event.extendedProps.customer_id || '',
                        first_name: firstName,
                        last_name: lastName,
                        email: event.extendedProps.customer_email || null,
                        phone: event.extendedProps.customer_phone || '',
                        business_id: selectedBusinessId || '',
                        type: 'person',
                        address: null,
                        source: null,
                        tags: [],
                        last_interaction: null,
                        lifetime_value: 0,
                        next_service_date: null,
                        referral_source: null,
                        preferences: {},
                        status: 'active',
                        amount_spent: 0,
                        credit_balance: 0,
                        notes: null,
                        tax_exempt: false,
                        workflow_exempt: false,
                        carfax_exempt: false,
                        receive_reminders: true,
                        receive_offers: true,
                        lat: null,
                        lng: null,
                        is_supplier: false,
                        repeat: false,
                        created_at: '',
                        updated_at: '',
                        vehicles: [],
                        wallet: 0
                      };
                      
                      return (
                        <BookingCustomerInfo
                          customer={customer}
                          bookingAddress={''}
                          mobile_booking={false}
                          canEdit={false}
                          businessId={selectedBusinessId}
                        />
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>
          );
  };

  const renderBookingsList = () => (
    <div className="space-y-3">
      {todaysEvents.length > 0 ? (
        todaysEvents.map((event, index) => renderFullBookingCard(event, index))
      ) : null}
      {/* Google Calendar events for this day */}
      {googleCalendarEvents
        .filter(ge => {
          if (!ge.start_time) return false;
          const geStart = parseISO(ge.start_time);
          return format(geStart, 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd');
        })
        .map((ge) => {
          const startTime = ge.start_time ? parseISO(ge.start_time) : new Date();
          const endTime = ge.end_time ? parseISO(ge.end_time) : new Date(startTime.getTime() + 3600000);
          return (
            <div
              key={`gcal-${ge.id}`}
              className="bg-white rounded-[12px] border border-blue-200/60 shadow-sm overflow-hidden cursor-pointer active:scale-[0.98] transition-all"
              onClick={() => onGcalEventClick?.(ge)}
            >
              <div className="p-3 sm:p-3.5 border-l-4 border-blue-400" style={{ backgroundColor: 'rgba(66, 133, 244, 0.08)' }}>
                <div className="flex items-start gap-2">
                  <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="h-4 w-4 text-blue-500" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11z"/>
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span className="text-[10px] font-medium text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-full">Google Calendar</span>
                    </div>
                    <p className="text-[15px] font-semibold text-gray-900 truncate">{ge.title || '(No title)'}</p>
                    <p className="text-[13px] text-gray-600 mt-0.5">
                      {ge.all_day ? 'All day' : `${format(startTime, 'h:mm a')} - ${format(endTime, 'h:mm a')}`}
                    </p>
                    {ge.location && (
                      <p className="text-[12px] text-gray-500 mt-1 flex items-center gap-1 truncate">
                        <MapPin className="h-3 w-3 flex-shrink-0" />{ge.location}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      {todaysEvents.length === 0 && googleCalendarEvents.filter(ge => ge.start_time && format(parseISO(ge.start_time), 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd')).length === 0 && (
        // Empty state
        <div className="flex flex-col items-center justify-center py-12 sm:py-16 px-4 sm:px-6">
          <div className="bg-gray-100 rounded-[12px] sm:rounded-[16px] p-5 sm:p-6 mb-3 sm:mb-4">
            <CalendarIcon className="h-10 w-10 sm:h-12 sm:w-12 text-gray-400" />
          </div>
          <h3 className="text-[16px] sm:text-[18px] font-semibold text-gray-900 mb-1.5 sm:mb-2" style={{ letterSpacing: '-0.01em' }}>No bookings today</h3>
          <p className="text-[13px] sm:text-[14px] text-gray-600 text-center max-w-md" style={{ letterSpacing: '-0.01em' }}>
            There are no bookings scheduled for this day.
          </p>
        </div>
      )}
    </div>
  );

  const renderBookingsListContent = () => (embedded ? renderEmbeddedBookingsList() : renderBookingsList());

  // Map view layout with bottom sheet
  if (mapViewEnabled) {
    return (
      <>
      <div
        className={
          embedded
            ? 'relative z-0 isolate h-full w-full min-h-0 overflow-hidden'
            : 'fixed inset-0 overflow-hidden'
        }
        style={
          embedded
            ? undefined
            : { top: 'calc(8.5rem + env(safe-area-inset-top, 0px))' }
        }
      >
        {/* Start below main app header + calendar day header so map and "On the Clock" widget never overlap (iOS) */}
        {/* CSS for pulsing animation */}
        <style>{`
          @keyframes pulse-ring {
            0% { transform: scale(0.8); opacity: 0.8; }
            50% { transform: scale(1.2); opacity: 0.3; }
            100% { transform: scale(0.8); opacity: 0.8; }
          }
        `}</style>

        {/* On the Clock Widget - full calendar day view only (hidden in control centre embedded schedule) */}
        {mapViewEnabled && !embedded && isAdminOrAdminResource && onTheClockResources.length > 0 && (
          <>
          {/* Collapsed tab - shows when widget is collapsed (full day view only) */}
          {!embedded && isClockWidgetCollapsed && (
            <button
              onClick={() => {
                setIsClockWidgetCollapsed(false);
                try { localStorage.setItem('clock_widget_collapsed', 'false'); } catch {}
              }}
              className="absolute z-10 flex items-center gap-1 bg-white border border-gray-200 shadow-lg rounded-l-lg px-2 py-3 hover:bg-gray-50 active:bg-gray-100 transition-all touch-manipulation"
              style={{
                top: 'max(1rem, env(safe-area-inset-top, 0px))',
                right: 0,
              }}
              title="Show On the Clock"
            >
              <ChevronLeft className="h-4 w-4 text-gray-500" />
              <span className="text-xs font-semibold text-gray-700 [writing-mode:vertical-lr] rotate-180">
                On the Clock ({onTheClockResources.length})
              </span>
            </button>
          )}

          {/* Expanded widget — always visible in embedded schedule */}
          {(embedded || !isClockWidgetCollapsed) && (
          <div
            className={`absolute ${embedded ? 'z-[2]' : 'z-10'} bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden flex ${
              embedded
                ? 'max-w-[min(11rem,46vw)] max-h-[120px] sm:max-w-[200px] sm:max-h-[160px]'
                : 'max-w-[280px] max-h-[400px]'
            }`}
            style={{
              top: embedded ? '0.375rem' : 'max(1rem, env(safe-area-inset-top, 0px))',
              left: embedded ? '0.375rem' : undefined,
              right: embedded ? undefined : 'max(1rem, env(safe-area-inset-right, 0px))',
            }}
          >
            {!embedded && (
            <button
              onClick={() => {
                setIsClockWidgetCollapsed(true);
                try { localStorage.setItem('clock_widget_collapsed', 'true'); } catch {}
              }}
              className="flex-shrink-0 w-5 flex items-center justify-center bg-gray-50 hover:bg-gray-100 active:bg-gray-200 border-r border-gray-200 transition-colors cursor-pointer touch-manipulation"
              title="Collapse"
            >
              <ChevronRight className="h-3.5 w-3.5 text-gray-400" />
            </button>
            )}

            <div
              className={`flex-1 overflow-y-auto ios-momentum-scroll ${
                embedded ? 'p-1.5 max-h-[120px] sm:p-2 sm:max-h-[160px]' : 'p-3 max-h-[400px]'
              }`}
            >
            <div className="flex items-center justify-between mb-1.5 sm:mb-2">
              <h3
                className={`font-semibold text-gray-900 ${embedded ? 'text-[10px] sm:text-xs' : 'text-sm'}`}
                style={{ letterSpacing: '-0.01em' }}
              >
                On the Clock
              </h3>
              <span className="text-[10px] sm:text-xs font-medium text-gray-500 bg-gray-100 px-1.5 py-0.5 sm:px-2 rounded-full">
                {onTheClockResources.length}
              </span>
            </div>
            
            <div className="space-y-2">
                {onTheClockResources.map((entry: any) => {
                  // Handle both 'resources' (plural) and 'resource' (singular) from Supabase join
                  const resourceData = entry.resources || entry.resource;
                  const resourceName = resourceData
                    ? `${resourceData.first_name || ''} ${resourceData.last_name || ''}`.trim()
                    : 'Unknown Resource';
                  
                  const initials = resourceName
                    .split(' ')
                    .map(n => n[0])
                    .join('')
                    .toUpperCase()
                    .slice(0, 2);
                  
                  // Calculate live elapsed time using currentTime state
                  const clockInTime = new Date(entry.clock_in);
                  const elapsedMs = currentTime.getTime() - clockInTime.getTime();
                  const totalSeconds = Math.floor(elapsedMs / 1000);
                  const hours = Math.floor(totalSeconds / 3600);
                  const minutes = Math.floor((totalSeconds % 3600) / 60);
                  const seconds = totalSeconds % 60;
                  
                  // Format time display
                  const formatElapsedTime = () => {
                    if (hours > 0) {
                      return `${hours}h ${minutes}m`;
                    } else if (minutes > 0) {
                      return `${minutes}m ${seconds}s`;
                    } else {
                      return `${seconds}s`;
                    }
                  };
                  
                  // Location data for map panning (may or may not exist)
                  const locationData = resourceLocations.get(entry.resource_id);
                  // Indicator is always green while clocked in (they're in onTheClockResources)
                  const isLocationActive = true;
                  
                  // Activity label helper
                  const activityLabel = (a: string | null | undefined) => {
                    if (!a || a === 'unknown') return null;
                    const map: Record<string, { emoji: string; label: string }> = {
                      still: { emoji: '🧍', label: 'Still' },
                      walking: { emoji: '🚶', label: 'Walking' },
                      on_foot: { emoji: '🚶', label: 'Walking' },
                      running: { emoji: '🏃', label: 'Running' },
                      on_bicycle: { emoji: '🚴', label: 'Cycling' },
                      in_vehicle: { emoji: '🚗', label: 'Driving' },
                    };
                    return map[a] || null;
                  };
                  const act = activityLabel(locationData?.activity);
                  const speedMph = locationData?.speed != null && locationData.speed > 0
                    ? (locationData.speed * 2.237).toFixed(0)
                    : null;

                  return (
                    <div
                      key={entry.id}
                      className="flex items-center gap-2 p-2 rounded-md hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => {
                        if (isLocationActive && locationData) {
                          focusResourcePin(entry.resource_id);
                        }
                      }}
                    >
                      {/* Avatar */}
                      <div className="flex-shrink-0 relative">
                        {resourceData?.avatar_url ? (
                          <img
                            src={resourceData.avatar_url}
                            alt={resourceName}
                            className="w-8 h-8 rounded-full object-cover border-2 border-gray-200"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center border-2 border-gray-200">
                            <span className="text-xs font-semibold text-white">
                              {initials}
                            </span>
                          </div>
                        )}
                        {/* Location indicator - positioned at bottom right of avatar */}
                        <div className="absolute -bottom-0.5 -right-0.5 bg-white rounded-full p-0.5">
                          <Circle 
                            className={`w-3 h-3 ${isLocationActive ? 'fill-green-500 text-green-500' : 'fill-red-500 text-red-500'}`}
                            style={{ filter: isLocationActive ? 'drop-shadow(0 0 2px rgba(34, 197, 94, 0.5))' : 'none' }}
                          />
                        </div>
                      </div>
                      
                      {/* Name, time, and activity */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="text-xs font-medium text-gray-900 truncate" style={{ letterSpacing: '-0.01em' }}>
                            {resourceName}
                          </p>
                        </div>
                        {(() => {
                          const jobEntry = activeJobEntries.find(j => j.resource_id === entry.resource_id);
                          if (jobEntry) {
                            return (
                              <p className="text-[10px] text-blue-600 font-medium truncate mt-0.5">
                                #{jobEntry.booking_number} {jobEntry.booking_title || ''}
                              </p>
                            );
                          }
                          return null;
                        })()}
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <p className="text-[10px] text-gray-500 font-mono">
                            {formatElapsedTime()}
                          </p>
                          {locationData?.odometer != null && locationData.odometer > 0 && (
                            <span className="text-[10px] text-gray-500 font-mono">
                              · {(locationData.odometer / 1000).toFixed(1)} km
                            </span>
                          )}
                          {act && (
                            <span className="text-[10px] text-gray-500">
                              {act.emoji} {act.label}{speedMph ? ` · ${speedMph} mph` : ''}
                            </span>
                          )}
                        </div>
                      </div>
                      {/* Chevron if location available */}
                      {isLocationActive && (
                        <MapPin className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          )}
          </>
        )}

        {/* Map Background - Google Maps */}
        <div
          ref={mapWrapperRef}
          className={`absolute inset-0 ${embedded ? 'control-centre-map z-0' : ''}`}
        >
          {isMapLoaded ? (
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              options={mapOptions}
              onLoad={onMapLoad}
            >
              {/* Current Location with Pulsing Effect - LIVE */}
              {currentLocation && (() => {
                const myLocData = currentResource?.id ? resourceLocations.get(currentResource.id) : null;
                return (
                  <OverlayView
                    position={currentLocation}
                    mapPaneName={OverlayView.OVERLAY_LAYER}
                  >
                    <div 
                      className="cursor-pointer"
                      onClick={() => setSelectedResourceMarker(myLocData ? { ...myLocData, _isSelf: true } : { lat: currentLocation.lat, lng: currentLocation.lng, _isSelf: true })}
                      style={{ 
                        transform: 'translate(-50%, -50%)',
                        position: 'relative',
                        width: '60px',
                        height: '60px'
                      }}
                    >
                      {/* Outer pulsing ring */}
                      <div 
                        className="absolute inset-0 rounded-full bg-blue-500/20 border-2 border-blue-500/40"
                        style={{ animation: 'pulse-ring 2s ease-in-out infinite' }}
                      />
                      {/* Middle pulsing ring */}
                      <div 
                        className="absolute rounded-full bg-blue-500/30 border border-blue-500/50"
                        style={{ width: '40px', height: '40px', top: '10px', left: '10px', animation: 'pulse-ring 2s ease-in-out infinite', animationDelay: '0.5s' }}
                      />
                      {/* Inner solid dot */}
                      <div 
                        className="absolute rounded-full bg-blue-500 border-2 border-white shadow-lg"
                        style={{ width: '20px', height: '20px', top: '20px', left: '20px', boxShadow: '0 0 10px rgba(59, 130, 246, 0.5), 0 2px 4px rgba(0,0,0,0.3)' }}
                      />
                    </div>
                  </OverlayView>
                );
              })()}

              {/* Markers for resource locations (excluding current user's own location) */}
              {(() => {
                const filteredLocations = Array.from(resourceLocations.values())
                  .filter((location) => {
                    // Filter out current user's own location (they see it via currentLocation overlay)
                    if (currentResource?.id && location.resource_id === currentResource.id) {
                      return false;
                    }
                    // Verify business_id matches (important for multi-business scenarios)
                    if (location.business_id !== selectedBusinessId) {
                      return false;
                    }
                    // Only show pin if the resource is currently clocked in
                    const isClockedIn = onTheClockResources.some(
                      (entry: any) => entry.resource_id === location.resource_id
                    );
                    if (!isClockedIn) {
                      return false;
                    }
                    return true;
                  });
                
                // Debug log
                console.log('📍 Rendering resource markers:', {
                  total: filteredLocations.length,
                  markers: filteredLocations.map(l => ({
                    resource_id: l.resource_id,
                    name: l.resources ? `${l.resources.first_name} ${l.resources.last_name}` : 'Unknown',
                    business_id: l.business_id
                  }))
                });
                
                return filteredLocations.map((location) => {
                  return (
                    <Marker
                      key={`resource-${location.resource_id}`}
                      position={{ lat: location.lat, lng: location.lng }}
                      onClick={() => setSelectedResourceMarker(location)}
                      icon={{
                        path: google.maps.SymbolPath.CIRCLE,
                        fillColor: '#3b82f6',
                        fillOpacity: 1,
                        strokeColor: '#ffffff',
                        strokeWeight: 2,
                        scale: 8,
                      }}
                    />
                  );
                });
              })()}

              {/* Info popup for selected resource marker */}
              {selectedResourceMarker && (
                <OverlayView
                  position={{ lat: selectedResourceMarker.lat, lng: selectedResourceMarker.lng }}
                  mapPaneName={OverlayView.FLOAT_PANE}
                >
                  <div
                    className="bg-white rounded-xl shadow-xl border border-gray-200 p-3 min-w-[180px]"
                    style={{ transform: 'translate(-50%, calc(-100% - 16px))', position: 'relative', zIndex: 9999 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      onClick={(e) => { e.stopPropagation(); setSelectedResourceMarker(null); }}
                      className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors z-10"
                    >
                      <span className="text-gray-500 text-sm font-medium">×</span>
                    </button>
                    <h3 className="font-semibold text-gray-900 text-sm pr-6">
                      {selectedResourceMarker._isSelf
                        ? 'You'
                        : selectedResourceMarker.resources
                          ? `${selectedResourceMarker.resources.first_name} ${selectedResourceMarker.resources.last_name}`
                          : selectedResourceMarker.resource
                            ? `${selectedResourceMarker.resource.first_name} ${selectedResourceMarker.resource.last_name}`
                            : 'Resource'}
                    </h3>
                    {selectedResourceMarker.activity && selectedResourceMarker.activity !== 'unknown' && (
                      <p className="text-xs text-gray-600 mt-1 flex items-center gap-1">
                        <span>
                          {selectedResourceMarker.activity === 'still' && '🧍'}
                          {selectedResourceMarker.activity === 'walking' && '🚶'}
                          {selectedResourceMarker.activity === 'on_foot' && '🚶'}
                          {selectedResourceMarker.activity === 'running' && '🏃'}
                          {selectedResourceMarker.activity === 'on_bicycle' && '🚴'}
                          {selectedResourceMarker.activity === 'in_vehicle' && '🚗'}
                        </span>
                        <span className="capitalize">
                          {selectedResourceMarker.activity.replace('_', ' ').replace('on foot', 'walking').replace('in vehicle', 'driving')}
                        </span>
                      </p>
                    )}
                    {selectedResourceMarker.speed != null && selectedResourceMarker.speed > 0 && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        {(selectedResourceMarker.speed * 3.6).toFixed(0)} km/h / {(selectedResourceMarker.speed * 2.237).toFixed(0)} mph
                      </p>
                    )}
                    {selectedResourceMarker.accuracy != null && (
                      <p className="text-xs text-gray-400 mt-0.5">
                        Accuracy: ±{Math.round(selectedResourceMarker.accuracy)}m
                      </p>
                    )}
                    {(selectedResourceMarker.gps_timestamp || selectedResourceMarker.created_at) && (
                      <p className="text-xs text-gray-400 mt-0.5">
                        Updated {(() => {
                          const ts = selectedResourceMarker.gps_timestamp || selectedResourceMarker.created_at;
                          const secs = Math.round((Date.now() - new Date(ts).getTime()) / 1000);
                          if (secs < 60) return `${secs}s ago`;
                          if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
                          return `${Math.round(secs / 3600)}h ago`;
                        })()}
                      </p>
                    )}
                  </div>
                </OverlayView>
              )}

              {/* Booking pins are rendered via native google.maps.Marker (see bookingMapPins effect) */}

              {/* Sales address markers - only when setting is enabled */}
              {salesPinsEnabledProp && salesPins.map(pin => {
                const position = { lat: pin.latitude, lng: pin.longitude };
                return (
                  <Marker
                    key={`sales-${pin.id}`}
                    position={position}
                    onClick={() => {
                      setSelectedMarker(null);
                      setSelectedResourceMarker(null);
                      setSelectedSalesPin(pin);
                      setSelectedSalesPinPosition(position);
                    }}
                    icon={addressMarkerService.getSalesPinIcon(
                      pin.marker_type,
                      addressMarkerService.getSalesPinInitials(pin),
                    )}
                  />
                );
              })}

              {/* Sales pin popup */}
              {salesPinsEnabledProp && selectedSalesPin && selectedSalesPinPosition && (
                <OverlayView
                  position={selectedSalesPinPosition}
                  mapPaneName={OverlayView.FLOAT_PANE}
                >
                  <div
                    className="bg-white rounded-xl shadow-xl border border-gray-200 p-3 min-w-[240px] max-w-[300px]"
                    style={{ transform: 'translate(-50%, calc(-100% - 12px))', position: 'relative', zIndex: 9999 }}
                    onClick={e => e.stopPropagation()}
                  >
                    <button
                      onClick={() => { setSelectedSalesPin(null); setSelectedSalesPinPosition(null); }}
                      className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors z-10"
                    >
                      <span className="text-gray-500 text-sm font-medium">×</span>
                    </button>
                    {selectedSalesPin.address && (
                      <p className="text-xs text-gray-600 mb-1 pr-6">{selectedSalesPin.address}</p>
                    )}
                    {selectedSalesPin.customer && (
                      <p className="text-xs text-gray-800 font-medium mb-1">
                        {selectedSalesPin.customer.first_name} {selectedSalesPin.customer.last_name}
                      </p>
                    )}
                    {(selectedSalesPin.google_place_name || selectedSalesPin.google_place_phone || selectedSalesPin.google_place_website) && (
                      <div className="mb-2 px-2.5 py-2 bg-blue-50/70 border border-blue-100 rounded-lg">
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-blue-500 mb-0.5">Google Business</p>
                        {selectedSalesPin.google_place_name && (
                          <p className="text-xs font-semibold text-gray-900">{selectedSalesPin.google_place_name}</p>
                        )}
                        {selectedSalesPin.google_place_phone && (
                          <a
                            href={`tel:${selectedSalesPin.google_place_phone}`}
                            onClick={e => e.stopPropagation()}
                            className="block text-xs text-blue-700 mt-0.5"
                          >
                            {selectedSalesPin.google_place_phone}
                          </a>
                        )}
                        {selectedSalesPin.google_place_website && (
                          <a
                            href={selectedSalesPin.google_place_website}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={e => e.stopPropagation()}
                            className="block text-xs text-blue-700 mt-0.5 truncate"
                          >
                            {selectedSalesPin.google_place_website}
                          </a>
                        )}
                      </div>
                    )}
                    {selectedSalesPin.notes && (
                      <p className="text-xs text-gray-500 mb-2 whitespace-pre-wrap">{selectedSalesPin.notes}</p>
                    )}

                    {/* Status selector */}
                    <div className="grid grid-cols-2 gap-1 mb-2">
                      {([
                        { type: 'not-home' as const, label: 'Not Home', color: '#9CA3AF' },
                        { type: 'prospect' as const, label: 'Prospect', color: '#F97316' },
                        { type: 'booked' as const, label: 'Closed', color: '#10B981' },
                        { type: 'comeback-later' as const, label: 'Come Back', color: '#F59E0B' },
                        { type: 'interested' as const, label: 'Interested', color: '#3B82F6' },
                        { type: 'estimate-sent' as const, label: 'Est. Sent', color: '#8B5CF6' },
                        { type: 'bad-customer' as const, label: 'Bad Cust.', color: '#EF4444' },
                        { type: 'customer' as const, label: 'Customer', color: '#06B6D4' },
                        { type: 'not-interested' as const, label: 'Not Int.', color: '#DC2626' },
                      ]).map(mt => (
                        <button
                          key={mt.type}
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (!selectedSalesPin.id || mt.type === selectedSalesPin.marker_type) return;
                            try {
                              await addressMarkerService.updateMarker(selectedSalesPin.id, { marker_type: mt.type });
                              setSalesPins(prev => prev.map(p => p.id === selectedSalesPin.id ? { ...p, marker_type: mt.type } : p));
                              setSelectedSalesPin(prev => prev ? { ...prev, marker_type: mt.type } : null);
                            } catch (err) {
                              console.error('Error updating pin status:', err);
                            }
                          }}
                          className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-[11px] font-medium transition-colors ${
                            selectedSalesPin.marker_type === mt.type
                              ? 'bg-gray-100 ring-2 ring-offset-1'
                              : 'hover:bg-gray-50'
                          }`}
                          style={selectedSalesPin.marker_type === mt.type ? { '--tw-ring-color': mt.color } as React.CSSProperties : undefined}
                        >
                          <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: mt.color }} />
                          {mt.label}
                        </button>
                      ))}
                    </div>

                    {selectedSalesPin.opportunity_id && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setViewLeadOpportunityId(Number(selectedSalesPin.opportunity_id));
                        }}
                        className="w-full mb-1.5 px-3 py-2 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-1"
                      >
                        <Star className="h-3 w-3" />
                        View Lead
                      </button>
                    )}

                    {selectedSalesPin.address && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(selectedSalesPin.address || '')}`, '_blank');
                        }}
                        className="w-full px-3 py-2 bg-green-500 text-white text-xs font-medium rounded-lg hover:bg-green-600 transition-colors flex items-center justify-center gap-1"
                      >
                        <Navigation className="h-3 w-3" />
                        Navigate
                      </button>
                    )}

                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (!selectedSalesPin.id) return;
                        if (!window.confirm('Delete this sales pin?')) return;
                        try {
                          await addressMarkerService.deleteMarker(selectedSalesPin.id);
                          setSalesPins(prev => prev.filter(p => p.id !== selectedSalesPin.id));
                          setSelectedSalesPin(null);
                          setSelectedSalesPinPosition(null);
                        } catch (err) {
                          console.error('Error deleting sales pin:', err);
                        }
                      }}
                      className="w-full mt-1.5 px-3 py-2 bg-red-50 text-red-600 text-xs font-medium rounded-lg hover:bg-red-100 transition-colors flex items-center justify-center gap-1"
                    >
                      <Trash2 className="h-3 w-3" />
                      Delete Pin
                    </button>
                    <div className="absolute left-1/2 -bottom-2 w-4 h-4 bg-white border-r border-b border-gray-200 -translate-x-1/2 rotate-45" style={{ zIndex: -1 }} />
                  </div>
                </OverlayView>
              )}

              {/* InfoWindow for selected marker */}
              {selectedMarker && (() => {
                const position = getEventMapPosition(selectedMarker);
                if (!position) return null;

                const startTime = new Date(selectedMarker.start);
                const endTime = selectedMarker.end ? new Date(selectedMarker.end) : new Date(startTime.getTime() + 60 * 60 * 1000);

                return (
                  <OverlayView
                    position={position}
                    mapPaneName={OverlayView.FLOAT_PANE}
                  >
                    <div 
                      className="bg-white rounded-xl shadow-xl border border-gray-200 p-3 min-w-[220px]"
                      style={{ 
                        transform: 'translate(-50%, calc(-100% - 12px))',
                        position: 'relative',
                        zIndex: 9999
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {/* Close button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedMarker(null);
                        }}
                        className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors z-10"
                      >
                        <span className="text-gray-500 text-sm font-medium">×</span>
                      </button>
                      
                      {/* Content */}
                      <h3 className="font-semibold text-gray-900 text-sm mb-1 pr-6">
                        {selectedMarker.extendedProps?.is_field_quote || selectedMarker.extendedProps?.is_task
                          ? selectedMarker.title
                          : `#${selectedMarker.extendedProps.booking_number} ${selectedMarker.title}`}
                      </h3>
                      <p className="text-xs text-gray-600 mb-1">
                        {format(startTime, 'h:mm a')} - {format(endTime, 'h:mm a')}
                      </p>
                      {/* Customer name */}
                      {selectedMarker.extendedProps?.customer && (
                        <p className="text-xs text-gray-700 font-medium mb-1">
                          {String(selectedMarker.extendedProps.customer)}
                        </p>
                      )}
                      {(selectedMarker.extendedProps?.address || selectedMarker.extendedProps?.booking_address) && (
                        <p className="text-xs text-gray-500 mb-1">
                          {selectedMarker.extendedProps.address || selectedMarker.extendedProps.booking_address}
                        </p>
                      )}
                      {/* Active workers on this booking */}
                      {(() => {
                        const workers = activeJobEntries.filter(j => j.booking_id === selectedMarker.id);
                        if (workers.length === 0) return null;
                        return (
                          <div className="mb-2 p-1.5 bg-blue-50 border border-blue-200 rounded-lg">
                            <p className="text-[10px] font-semibold text-blue-700 mb-1">Active Workers</p>
                            <div className="flex flex-wrap gap-1">
                              {workers.map(w => {
                                const res = onTheClockResources.find(r => r.resource_id === w.resource_id);
                                const rd = (res as any)?.resources || (res as any)?.resource;
                                const name = rd ? `${rd.first_name || ''} ${rd.last_name || ''}`.trim() : 'Unknown';
                                return (
                                  <span key={w.id} className="inline-flex items-center gap-1 bg-white border border-blue-200 rounded-full px-2 py-0.5">
                                    <span className="w-4 h-4 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                                      <span className="text-[7px] font-bold text-white leading-none">
                                        {`${rd?.first_name?.[0] || ''}${rd?.last_name?.[0] || ''}`.toUpperCase()}
                                      </span>
                                    </span>
                                    <span className="text-[10px] font-medium text-gray-700">{name}</span>
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })()}
                      {/* Booking subtotal (before tax) */}
                      {selectedMarker.extendedProps?.subtotal ? (
                        <p className="text-sm font-bold text-gray-900 mb-3">
                          ${Number(selectedMarker.extendedProps.subtotal).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </p>
                      ) : <div className="mb-3" />}
                      <div className="flex gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onBookingClick({ event: selectedMarker });
                            setSelectedMarker(null);
                          }}
                          className="flex-1 px-3 py-2 bg-blue-500 text-white text-xs font-medium rounded-lg hover:bg-blue-600 transition-colors"
                        >
                          View Details
                        </button>
                        {(selectedMarker.extendedProps?.address || selectedMarker.extendedProps?.booking_address) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedMarker(null);
                              handleMapPinWithClockCheck(selectedMarker, e as unknown as React.MouseEvent);
                            }}
                            className="px-3 py-2 bg-green-500 text-white text-xs font-medium rounded-lg hover:bg-green-600 transition-colors flex items-center gap-1"
                          >
                            <Navigation className="h-3 w-3" />
                            Navigate
                          </button>
                        )}
                      </div>
                      
                      {/* Arrow pointer */}
                      <div 
                        className="absolute left-1/2 -bottom-2 w-4 h-4 bg-white border-r border-b border-gray-200 -translate-x-1/2 rotate-45"
                        style={{ zIndex: -1 }}
                      />
                    </div>
                  </OverlayView>
                );
              })()}
            </GoogleMap>
          ) : (
            // Loading state
            <div className="h-full flex flex-col items-center justify-center text-center p-6 bg-gray-100">
              <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4" />
              <p className="text-sm text-gray-600">Loading map...</p>
            </div>
          )}
        </div>

        {/* Bottom Sheet Container */}
        <div 
          className={`absolute bottom-0 left-1/2 -translate-x-1/2 w-full transition-all duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] ${embedded ? 'z-[3]' : 'z-30'} ${
            embedded
              ? 'max-sm:max-w-none max-w-sm max-sm:mx-0 mx-auto rounded-t-2xl sm:rounded-t-[20px] bg-white/92 backdrop-blur-2xl border-t border-white/60 shadow-[0_-8px_32px_rgba(0,0,0,0.12)]'
              : 'max-w-2xl rounded-t-3xl bg-white shadow-2xl border-t border-x border-gray-200'
          } ${
            bookingsExpanded
              ? embedded
                ? 'h-full'
                : 'h-[85%]'
              : embedded
                ? 'max-sm:h-[44px] sm:h-[48px]'
                : 'h-[72px]'
          }`}
        >
          {/* Drag Handle / Header */}
          <button
            onClick={() => setBookingsExpanded(!bookingsExpanded)}
            className={`w-full flex flex-col items-center cursor-pointer touch-manipulation ${
              embedded ? 'pt-1.5 pb-0.5 sm:pt-2 sm:pb-1' : 'pt-3 pb-2'
            }`}
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <div
              className={`rounded-full shrink-0 ${
                embedded
                  ? 'w-8 h-1 bg-black/15 mb-1.5 sm:w-9 sm:h-[5px] sm:mb-2'
                  : 'w-10 h-1 bg-gray-300 mb-3'
              }`}
            />

            {embedded ? (
              <div
                className="w-full flex items-center justify-between px-3 sm:px-4"
                style={{ fontFamily: embeddedIosFont }}
              >
                <p className="text-[13px] sm:text-[15px] font-semibold text-gray-900 tracking-tight truncate min-w-0">
                  Today&apos;s Bookings
                  <span className="font-normal text-gray-500"> · {todaysEvents.length}</span>
                </p>
                <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-black/[0.05] flex items-center justify-center shrink-0 ml-2">
                  {bookingsExpanded ? (
                    <ChevronDown className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-gray-500" strokeWidth={2.5} />
                  ) : (
                    <ChevronUp className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-gray-500" strokeWidth={2.5} />
                  )}
                </div>
              </div>
            ) : (
              <div className="w-full flex items-center justify-between px-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                    <CalendarIcon className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="text-left">
                    <h3 className="text-sm font-semibold text-gray-900">
                      Today&apos;s Bookings
                    </h3>
                    <p className="text-xs text-gray-500">
                      {todaysEvents.length} booking{todaysEvents.length !== 1 ? 's' : ''} scheduled
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {bookingsExpanded ? (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronUp className="h-5 w-5 text-gray-400" />
                  )}
                </div>
              </div>
            )}
          </button>

          {/* Bookings Content */}
          <div 
            className={`overflow-y-auto transition-all duration-300 ${
              embedded
                ? 'px-1.5 pb-1.5 pt-0.5 sm:px-2 sm:pb-2 ios-momentum-scroll overscroll-y-contain'
                : 'px-4 pb-4'
            } ${bookingsExpanded ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
            style={{
              height: bookingsExpanded
                ? embedded
                  ? 'calc(100% - 48px)'
                  : 'calc(100% - 72px)'
                : '0',
              WebkitOverflowScrolling: embedded ? 'touch' : undefined,
            }}
          >
            {renderBookingsListContent()}
          </div>
        </div>
      </div>

      {/* Clock-in prompt modal */}
      {clockInPromptOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4" onClick={() => { setClockInPromptOpen(false); pendingMapPinEvent.current = null; }}>
          <div className="fixed inset-0 bg-black/50" />
          <div
            className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mx-auto w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center mb-4">
              <AlertCircle className="h-7 w-7 text-amber-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">You're not clocked in</h3>
            <p className="text-sm text-gray-500 mb-6">Looks like you're not clocked in yet. Would you like to clock in before navigating?</p>
            <div className="flex gap-3">
              <button
                onClick={handleSkipClockIn}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 rounded-xl transition-colors"
              >
                Skip
              </button>
              <button
                onClick={handleClockInFromPrompt}
                disabled={clockInPromptLoading}
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-green-600 hover:bg-green-700 active:bg-green-800 rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Clock className="h-4 w-4" />
                {clockInPromptLoading ? 'Clocking in...' : 'Clock In'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Opportunity details for a linked sales pin */}
      {viewLeadOpportunityId != null && (
        <CrmEmbeddedOpportunityModal
          opportunityId={viewLeadOpportunityId}
          onClose={() => setViewLeadOpportunityId(null)}
        />
      )}
      </>
    );
  }
  
  // Standard view (non-map)
  return (
    <div
      className={
        embedded
          ? 'h-full overflow-y-auto px-1.5 py-1.5 sm:px-2 sm:py-2 w-full max-sm:max-w-none max-w-sm max-sm:mx-0 mx-auto ios-momentum-scroll overscroll-y-contain bg-[#f2f2f7]'
          : 'max-w-2xl mx-auto'
      }
    >
      {renderBookingsListContent()}

      {/* Clock-in prompt modal */}
      {clockInPromptOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4" onClick={() => { setClockInPromptOpen(false); pendingMapPinEvent.current = null; }}>
          <div className="fixed inset-0 bg-black/50" />
          <div
            className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mx-auto w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center mb-4">
              <AlertCircle className="h-7 w-7 text-amber-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">You're not clocked in</h3>
            <p className="text-sm text-gray-500 mb-6">Looks like you're not clocked in yet. Would you like to clock in before navigating?</p>
            <div className="flex gap-3">
              <button
                onClick={handleSkipClockIn}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 rounded-xl transition-colors"
              >
                Skip
              </button>
              <button
                onClick={handleClockInFromPrompt}
                disabled={clockInPromptLoading}
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-green-600 hover:bg-green-700 active:bg-green-800 rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Clock className="h-4 w-4" />
                {clockInPromptLoading ? 'Clocking in...' : 'Clock In'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Opportunity details for a linked sales pin */}
      {viewLeadOpportunityId != null && (
        <CrmEmbeddedOpportunityModal
          opportunityId={viewLeadOpportunityId}
          onClose={() => setViewLeadOpportunityId(null)}
        />
      )}
    </div>
  );
}
